import React, { useEffect, useState } from "react";
import PizZip from "pizzip";
import Docxtemplater from "docxtemplater";
import { saveAs } from "file-saver";
import ImageModule from "open-docxtemplater-image-module-2"; // [ok] ajout

export default function GenerateDoc({
  templateArrayBuffer,
  values = {},
  tagKeys = [],
  requiredKeys = [],
  fields = [],
  outputNameTemplate = "document_{{timestamp}}.docx",
  onAfterGenerate,
  auto = false,
  index = 0,
  total = 1,
}) {
  const [isGenerating, setIsGenerating] = useState(false);
//  console.log("[inbox] [GenerateDoc] Props reçues:", { index, total, auto, values });

  // Helpers (mm -> px) + pixel transparent (fallback)
  const mmToPx = (mm) => Math.round((mm / 25.4) * 96);
  const TRANSPARENT_PX_PNG = Uint8Array.from(
    atob("iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR4nGNgYAAAAAMAAWgmWQ0AAAAASUVORK5CYII="),
    (c) => c.charCodeAt(0)
  );

  // Fonction de génération
  const onGenerate = async () => {
    if (!templateArrayBuffer) {
      console.error("[x] [GenerateDoc] Aucun template fourni");
      return;
    }

    try {
      console.log("[rocket] [GenerateDoc] Lancement génération", {
        index,
        total,
        hasTemplate: !!templateArrayBuffer,
        values,
        tagKeys,
        requiredKeys,
      });

      setIsGenerating(true);

      const zip = new PizZip(templateArrayBuffer);

      // [ok] Module image (open-source)
      let getImageCalls = 0;
      const warnings = [];
      const imageModule = new ImageModule({
        fileType: "docx",
        centered: false, // utiliser {%%logo} dans Word si tu veux un bloc centré
        getImage: (tagValue /*, tagName */) => {
          getImageCalls++;
          if (tagValue?.bytes instanceof Uint8Array) return tagValue.bytes;
          if (tagValue instanceof Uint8Array) return tagValue;
          warnings.push(`getImage: valeur sans bytes valides -> pixel transparent`);
          return TRANSPARENT_PX_PNG;
        },
        getSize: (img /* Uint8Array */, tagValue /*, tagName */) => {
          const wmm = Number(tagValue?.wmm ?? 40);
          const hmm = Number(tagValue?.hmm ?? 20);
          return [mmToPx(wmm), mmToPx(hmm)];
        },
      });

      // [warning] Pas de delimiters "{{ }}" ici (on reste en {key} pour la pile gratuite)
      const doc = new Docxtemplater(zip, {
        paragraphLoop: true,
        linebreaks: true,
        nullGetter: () => "",
        modules: [imageModule], // [ok] branchement du module image
      });

      // Vérifier les champs requis
      const missing = [];
      requiredKeys.forEach((k) => {
        const v = values[k];
        if (v === "" || v === null || v === undefined) missing.push(k);
      });
      if (missing.length) {
        alert("Champs requis manquants :\n- " + missing.join("\n- "));
        setIsGenerating(false);
        return;
      }

      // Vérifier regex_validation
      const invalidRegex = [];
      fields.forEach((f) => {
        if (f.regex_validation && values[f.key]) {
          try {
            const re = new RegExp(f.regex_validation);
            if (!re.test(values[f.key])) {
              invalidRegex.push(f.label || f.key);
            }
          } catch (e) {
            console.warn(`Regex invalide ignorée pour ${f.key}`, e.message);
          }
        }
      });
      if (invalidRegex.length) {
        alert("Certains champs ne respectent pas le format attendu :\n- " + invalidRegex.join("\n- "));
        setIsGenerating(false);
        return;
      }

      // Injecter les valeurs
      const data = { ...values };
      tagKeys.forEach((k) => {
        if (data[k] == null) data[k] = "";
      });

      // [ok] Conversion des multi_select (arrays)
      Object.entries(data).forEach(([k, v]) => {
        if (Array.isArray(v)) {
          if (v.length > 0 && typeof v[0] === "object") {
            // Section répétitive -> garder tel quel
            data[k] = v;
          } else {
            // Multi-select -> join
            data[k] = v.join(", ");
          }
        }
      });

      // [search] Debug pré-rendu (images fournies)
      const imageKeys = Object.keys(data).filter(
        (k) => data[k] && typeof data[k] === "object" && (data[k].bytes instanceof Uint8Array)
      );
      //console.log("[GEN] image keys:", imageKeys);
      imageKeys.forEach((k) => {
        const it = data[k];
        //console.log(`[GEN] image ${k} -> bytesLen=${it.bytes?.length || 0}, wmm=${it.wmm}, hmm=${it.hmm}`);
      });

      //console.log("[key] Clés disponibles dans data:", Object.keys(data));
      //console.log("[search] Variables trouvées dans le template:", doc.getFullText());
      // [ok] Interpolation des {key} dans les valeurs texte (option B)
      // - On limite aux strings pour éviter d'abîmer images/objets
      // - 2 passes max pour gérer un léger chaînage {a} -> "...{b}..." -> "...valeur..."
      const interpolateString = (str, bag, maxPasses = 2) => {
        let out = String(str);
        for (let i = 0; i < maxPasses; i++) {
          const next = out.replace(/\{([a-zA-Z0-9_]+)\}/g, (_, k) => {
            const v = bag[k];
            if (v == null) return "";
            if (Array.isArray(v)) return v.join(", ");
            if (typeof v === "object") return ""; // évite "[object Object]" (ex: images)
            return String(v);
          });
          if (next === out) break;
          out = next;
        }
        return out;
      };

      // (Optionnel) cibler certaines clés à interpoler plutôt que tout le monde.
      // Ici on cible les champs dont la config indique un template "à insérer",
      // comme les checkboxes avec template_if_checked, ou clés finissant par _tpl.
      const interpolateKeys = new Set(
        (fields || [])
          .filter(f => f?.type === "checkbox" || f?.interpolate === true || /_tpl$/.test(f?.key || ""))
          .map(f => f.key)
      );

      // Appliquer l'interpolation (par défaut: seulement les clés ciblées ci-dessus).
      Object.entries(data).forEach(([k, v]) => {
        if (typeof v === "string" && (interpolateKeys.size === 0 || interpolateKeys.has(k))) {
          const before = v;
          const after  = interpolateString(v, data);
          if (before !== after) {
            //console.log(`[interpolate] ${k}:`, { before, after });
            data[k] = after;
          }
        }
      });

      // [ok] API stable en 3.50.0
      doc.setData(data);
      try {
        doc.render();
      } catch (e) {
        console.error("[x] [RENDER ERROR]", e);
        const errs = e?.properties?.errors || [];
        if (errs.length) {
          errs.forEach((err, i) => {
            console.error(`[TEMPLATE ERROR ${i}]`, err?.properties?.explanation || err?.message || err);
          });
        }
        alert("Erreur génération: voir la console (TEMPLATE ERROR).");
        setIsGenerating(false);
        return;
      }

      if (getImageCalls === 0) {
        warnings.push(
          "Aucune invocation de getImage -> vérifie que la balise image est bien '{%key}' (ou '{%%key}') et qu'elle est seule dans son paragraphe."
        );
      }
      if (warnings.length) console.warn("[GEN] warnings:", warnings);

      // Générer le fichier final
      const out = doc.getZip().generate({
        type: "blob",
        mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });

      const name = (outputNameTemplate || "document_{{timestamp}}.docx")
        .replace("{{timestamp}}", String(Date.now()))
        .replace(/\{\{([a-zA-Z0-9_\-\.]+)\}\}/g, (_, k) =>
          (values[k] ?? "").toString().trim() || k
        );

      //console.log("[disk] Sauvegarde du fichier:", name, "| getImage calls:", getImageCalls, "| blob size:", out.size);
      saveAs(out, name);

            // Callback (on remonte aussi le blob)
      if (typeof onAfterGenerate === "function") {
        //console.log("[mail] Appel du callback onAfterGenerate", `Index: ${index}/${total - 1}`);
        onAfterGenerate(name, out); // <-- out = Blob DOCX
      }

    } catch (err) {
      console.error("[x] [GenerateDoc] Erreur de génération:", err);
      alert("Erreur génération: " + err.message);
    } finally {
      setIsGenerating(false);
    }
  };

  // Auto exécution en mode batch
  useEffect(() => {
    if (auto) {
      //console.log("[flash] [GenerateDoc] Auto-génération déclenchée", { index, total });
      onGenerate();
    }
  }, [auto, index]);

  return (
    <div className="nav-buttons" style={{ paddingTop: 0, borderTop: 0 }}>
      {!auto && (
        <button className="btn btn-primary" onClick={onGenerate} disabled={isGenerating}>
          <i className="fas fa-file-export"></i>{" "}
          {isGenerating ? "Génération en cours..." : "Générer le DOCX"}
        </button>
      )}
      {auto && (
        <div style={{ marginTop: "10px" }}>
          <span>[hourglass] Génération automatique... Ligne {index + 1}/{total}</span>
        </div>
      )}
    </div>
  );
}
